// src/pages/ROEntryPage.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useROStore } from "../context/ROContext";
import {
  TRANSPORT_TYPES,
  WORK_MIX,
  PARTS_STATUS,
  RO_STATUS,
  RO_LIFECYCLE,
} from "../constants/roStatus";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function toUpperTrim(v) {
  return safeString(v).trim().toUpperCase();
}

function normalizeVinLast8(v) {
  return safeString(v).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

function normalizeRoNumber(v) {
  return safeString(v).trim().toUpperCase();
}

function normalizeTag(v) {
  return safeString(v).trim().toUpperCase().replace(/\s+/g, "");
}

function prettyTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function promiseDisplay(datetimeLocalValue) {
  if (!datetimeLocalValue) return "No promise time set";
  try {
    const d = new Date(datetimeLocalValue);
    if (Number.isNaN(d.getTime())) return "Invalid promise time";
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "Invalid promise time";
  }
}

function getTransportLabel(value) {
  const raw = safeString(value).trim().toUpperCase();
  if (raw === TRANSPORT_TYPES.WAITER || raw === "WAITER") return "Waiter";
  if (raw === TRANSPORT_TYPES.LOANER || raw === "LOANER") return "Loaner";
  if (raw === TRANSPORT_TYPES.SHUTTLE || raw === "SHUTTLE") return "Shuttle";
  return "Other";
}

export function roundTo15MinLocal(datetimeLocalValue) {
  if (!datetimeLocalValue) return "";
  const d = new Date(datetimeLocalValue);
  if (Number.isNaN(d.getTime())) return datetimeLocalValue;

  const step = 15 * 60 * 1000;
  const rounded = Math.round(d.getTime() / step) * step;
  const r = new Date(rounded);

  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = r.getFullYear();
  const mm = pad(r.getMonth() + 1);
  const dd = pad(r.getDate());
  const hh = pad(r.getHours());
  const mi = pad(r.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function FieldMessage({ id, error }) {
  if (!error) return null;

  return (
    <div id={id} style={styles.fieldError} role="alert">
      {error}
    </div>
  );
}

function StatusPill({ tone = "neutral", children }) {
  const palette = {
    neutral: {
      border: "rgba(15,23,42,0.10)",
      bg: "rgba(248,250,252,0.94)",
      color: "rgba(15,23,42,0.68)",
    },
    good: {
      border: "rgba(6,118,71,0.14)",
      bg: "rgba(236,253,243,0.94)",
      color: "#067647",
    },
    warn: {
      border: "rgba(181,71,8,0.14)",
      bg: "rgba(255,247,237,0.94)",
      color: "#b54708",
    },
    danger: {
      border: "rgba(180,35,24,0.14)",
      bg: "rgba(254,243,242,0.96)",
      color: "#b42318",
    },
    working: {
      border: "rgba(37,99,235,0.14)",
      bg: "rgba(239,246,255,0.94)",
      color: "#1d4ed8",
    },
  };

  const p = palette[tone] || palette.neutral;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 24,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${p.border}`,
        background: p.bg,
        color: p.color,
        fontSize: 10.5,
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

export default function ROEntryPage() {
  const store = useROStore();
  const navigate = useNavigate();

  const validateTagNumberFn = store?.validateTagNumber;
  const addROFn = store?.addRO;

  const [roNumber, setRoNumber] = useState("");
  const [tagNumber, setTagNumber] = useState("");
  const [vinLast8, setVinLast8] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [advisorName, setAdvisorName] = useState("");
  const [advisorEmploymentId, setAdvisorEmploymentId] = useState("");

  const [workMix, setWorkMix] = useState(WORK_MIX.MAINTENANCE);
  const [transportType, setTransportType] = useState(TRANSPORT_TYPES.OTHER);
  const [promiseTimeISO, setPromiseTimeISO] = useState("");
  const promiseInputRef = useRef(null);
  const activeStoreId = store?.settings?.storeId;

  useEffect(() => {
    if (promiseInputRef.current) promiseInputRef.current.value = "";
    setPromiseTimeISO("");
  }, [activeStoreId]);

  function commitPromiseTime() {
    const input = promiseInputRef.current;
    if (!input || input.validity.badInput) return;
    const committed = roundTo15MinLocal(input.value);
    input.value = committed;
    setPromiseTimeISO(committed);
    return committed;
  }

  const [tagTouched, setTagTouched] = useState(false);
  const [tagSubmitAttempted, setTagSubmitAttempted] = useState(false);
  const [tagError, setTagError] = useState("");

  const [roTouched, setRoTouched] = useState(false);
  const [roSubmitAttempted, setRoSubmitAttempted] = useState(false);
  const [roError, setRoError] = useState("");

  const [vinTouched, setVinTouched] = useState(false);
  const [vinSubmitAttempted, setVinSubmitAttempted] = useState(false);
  const [vinError, setVinError] = useState("");

  const [customerTouched, setCustomerTouched] = useState(false);
  const [customerSubmitAttempted, setCustomerSubmitAttempted] = useState(false);
  const [customerError, setCustomerError] = useState("");

  const [vehicleTouched, setVehicleTouched] = useState(false);
  const [vehicleSubmitAttempted, setVehicleSubmitAttempted] = useState(false);
  const [vehicleError, setVehicleError] = useState("");

  const [advisorTouched, setAdvisorTouched] = useState(false);
  const [advisorSubmitAttempted, setAdvisorSubmitAttempted] = useState(false);
  const [advisorError, setAdvisorError] = useState("");

  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [recentAdded, setRecentAdded] = useState(null);

  const currentUser = store?.currentUser;
  const isSavingGlobal = !!store?.isSaving;
  const isImpersonating = !!store?.isImpersonating;

  const busy = saving || isSavingGlobal;

  const defaultAdvisor = useMemo(() => {
    const name = safeString(currentUser?.name).trim();
    return name || "";
  }, [currentUser?.name]);

  const roList = useMemo(() => {
    return Array.isArray(store?.ros)
      ? store.ros
      : Array.isArray(store?.repairOrders)
      ? store.repairOrders
      : [];
  }, [store?.ros, store?.repairOrders]);

  const advisorOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const seenAccounts = new Set();

    const addOption = (option) => {
      const value = safeString(option?.value).trim();
      const label = safeString(option?.label).trim();
      if (!value || !label || seen.has(value)) return;
      seen.add(value);
      options.push({ value, label, name: safeString(option?.name || label).trim(), employmentId: safeString(option?.employmentId).trim() });
    };

    const addLegacyName = (x) => {
      const name = safeString(x).trim();
      if (name && !options.some((option) => option.name === name)) addOption({ value: `name:${name}`, label: name, name, employmentId: "" });
    };

    const directory = Array.isArray(store?.staffDirectory)
      ? store.staffDirectory
      : [];

    // Keep the selected employment when an account has several eligible relationships.
    [...directory].sort((a, b) => {
      if (a.id === advisorEmploymentId) return -1;
      if (b.id === advisorEmploymentId) return 1;
      return safeString(a.id).localeCompare(safeString(b.id));
    }).forEach((u) => {
      if (activeStoreId && u?.businessUnitId && String(u.businessUnitId) !== String(activeStoreId)) return;
      const role = safeString(u?.role).toLowerCase().trim();
      const employmentStatus = safeString(
        u?.employmentStatus ?? u?.employment_status
      )
        .toUpperCase()
        .trim();

      const isActive =
        u?.active === undefined ? employmentStatus !== "TERMINATED" : !!u.active;

      const isAdvisor =
        role === "advisor" ||
        role === "service_advisor" ||
        role === "service advisor";

      const employmentId = safeString(u?.id).trim();
      const name = safeString(u?.name).trim();
      if (isAdvisor && isActive && employmentId && name) {
        const accountId = safeString(u?.appUserId || employmentId);
        if (seenAccounts.has(accountId)) return;
        seenAccounts.add(accountId);
        addOption({ value: `staff:${employmentId}`, label: name, name, employmentId });
      }
    });

    addLegacyName(defaultAdvisor);
    roList.forEach((r) => addLegacyName(r?.advisorName));

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [roList, store?.staffDirectory, defaultAdvisor, activeStoreId, advisorEmploymentId]);

  useEffect(() => {
    if (!advisorName && defaultAdvisor) setAdvisorName(defaultAdvisor);
  }, [defaultAdvisor, advisorName]);

  const matchingAdvisor = advisorOptions.filter((option) => option.name === advisorName);
  const advisorSelectValue = advisorEmploymentId
    ? `staff:${advisorEmploymentId}`
    : advisorName
    ? (matchingAdvisor.length === 1 ? matchingAdvisor[0].value : `name:${advisorName}`)
    : "";

  const applyAdvisorSelection = useCallback(
    (value) => {
      const selected = advisorOptions.find((option) => option.value === value);
      if (selected?.employmentId) {
        setAdvisorName(selected.name);
        setAdvisorEmploymentId(selected.employmentId);
        return;
      }

      const name = selected?.name || safeString(value).replace(/^name:/, "");
      setAdvisorName(name);
      setAdvisorEmploymentId("");
    },
    [advisorOptions]
  );

  useEffect(() => {
    if (!banner || banner.type !== "success") return;
    const t = setTimeout(() => setBanner(null), 4200);
    return () => clearTimeout(t);
  }, [banner]);

  function workMixLabel(wm) {
    const s = String(wm || "").toUpperCase();
    if (s === "MAINTENANCE") return "Maintenance";
    if (s === "DIAGNOSIS") return "Diagnosis";
    return "Other";
  }

  const validateRoNow = useCallback(
    (raw) => {
      const rn = normalizeRoNumber(raw);
      if (!rn) return { ok: false, message: "RO number is required." };

      const exists = roList.some((r) => toUpperTrim(r?.roNumber) === rn);

      return exists
        ? { ok: false, message: `RO #${rn} already exists on an active RO.` }
        : { ok: true };
    },
    [roList]
  );

  const validateTagNow = useCallback(
    (raw) => {
      const tag = normalizeTag(raw);
      if (!tag) return { ok: false, message: "Tag # is required." };

      if (typeof validateTagNumberFn === "function") {
        const res = validateTagNumberFn(tag);
        return res?.ok
          ? { ok: true }
          : {
              ok: false,
              message: res?.message || "Tag # is already in use on an active RO.",
            };
      }

      const exists = roList.some((r) => toUpperTrim(r?.tagNumber) === tag);

      return exists
        ? { ok: false, message: "Tag # is already in use on an active RO." }
        : { ok: true };
    },
    [validateTagNumberFn, roList]
  );

  const validateVinNow = useCallback((raw) => {
    const vin = normalizeVinLast8(raw);
    if (!vin) return { ok: false, message: "VIN Last 8 is required." };

    if (vin.length !== 8) {
      return { ok: false, message: "VIN Last 8 must be exactly 8 characters." };
    }

    return { ok: true };
  }, []);

  const validateCustomerNow = useCallback((raw) => {
    const name = safeString(raw).trim();
    if (!name) return { ok: false, message: "Customer name is required." };
    return { ok: true };
  }, []);

  const validateVehicleNow = useCallback((raw) => {
    const val = safeString(raw).trim();
    if (!val) return { ok: false, message: "Vehicle is required." };
    return { ok: true };
  }, []);

  const validateAdvisorNow = useCallback((raw) => {
    const val = safeString(raw).trim();
    if (!val) return { ok: false, message: "Advisor name is required." };
    return { ok: true };
  }, []);

  useEffect(() => {
    if (saving) {
      setRoError("");
      return;
    }

    const shouldShow = roTouched || roSubmitAttempted;
    if (!shouldShow) {
      setRoError("");
      return;
    }

    const res = validateRoNow(roNumber);
    setRoError(res.ok ? "" : res.message);
  }, [saving, roNumber, roTouched, roSubmitAttempted, validateRoNow]);

  useEffect(() => {
    if (saving) {
      setTagError("");
      return;
    }

    const shouldShow = tagTouched || tagSubmitAttempted;
    if (!shouldShow) {
      setTagError("");
      return;
    }

    const res = validateTagNow(tagNumber);
    setTagError(res.ok ? "" : res.message);
  }, [saving, tagNumber, tagTouched, tagSubmitAttempted, validateTagNow]);

  useEffect(() => {
    if (saving) {
      setVinError("");
      return;
    }

    const shouldShow = vinTouched || vinSubmitAttempted;
    if (!shouldShow) {
      setVinError("");
      return;
    }

    const res = validateVinNow(vinLast8);
    setVinError(res.ok ? "" : res.message);
  }, [saving, vinLast8, vinTouched, vinSubmitAttempted, validateVinNow]);

  useEffect(() => {
    if (saving) {
      setCustomerError("");
      return;
    }

    const shouldShow = customerTouched || customerSubmitAttempted;
    if (!shouldShow) {
      setCustomerError("");
      return;
    }

    const res = validateCustomerNow(customerName);
    setCustomerError(res.ok ? "" : res.message);
  }, [
    saving,
    customerName,
    customerTouched,
    customerSubmitAttempted,
    validateCustomerNow,
  ]);

  useEffect(() => {
    if (saving) {
      setVehicleError("");
      return;
    }

    const shouldShow = vehicleTouched || vehicleSubmitAttempted;
    if (!shouldShow) {
      setVehicleError("");
      return;
    }

    const res = validateVehicleNow(vehicle);
    setVehicleError(res.ok ? "" : res.message);
  }, [saving, vehicle, vehicleTouched, vehicleSubmitAttempted, validateVehicleNow]);

  useEffect(() => {
    if (saving) {
      setAdvisorError("");
      return;
    }

    const shouldShow = advisorTouched || advisorSubmitAttempted;
    if (!shouldShow) {
      setAdvisorError("");
      return;
    }

    const res = validateAdvisorNow(advisorName);
    setAdvisorError(res.ok ? "" : res.message);
  }, [
    saving,
    advisorName,
    advisorTouched,
    advisorSubmitAttempted,
    validateAdvisorNow,
  ]);

  const formProgress = useMemo(() => {
    const checks = [
      validateRoNow(roNumber).ok,
      validateTagNow(tagNumber).ok,
      validateVinNow(vinLast8).ok,
      validateCustomerNow(customerName).ok,
      validateVehicleNow(vehicle).ok,
      validateAdvisorNow(advisorName).ok,
    ];

    const done = checks.filter(Boolean).length;

    return {
      done,
      total: checks.length,
      percent: Math.round((done / checks.length) * 100),
    };
  }, [
    roNumber,
    tagNumber,
    vinLast8,
    customerName,
    vehicle,
    advisorName,
    validateRoNow,
    validateTagNow,
    validateVinNow,
    validateCustomerNow,
    validateVehicleNow,
    validateAdvisorNow,
  ]);

  const isFormReady = formProgress.done === formProgress.total;

  function clearErrorBannerIfAny() {
    if (banner?.type === "error") setBanner(null);
  }

  function resetValidationUi() {
    setRoError("");
    setTagError("");
    setVinError("");
    setCustomerError("");
    setVehicleError("");
    setAdvisorError("");

    setRoTouched(false);
    setTagTouched(false);
    setVinTouched(false);
    setCustomerTouched(false);
    setVehicleTouched(false);
    setAdvisorTouched(false);

    setRoSubmitAttempted(false);
    setTagSubmitAttempted(false);
    setVinSubmitAttempted(false);
    setCustomerSubmitAttempted(false);
    setVehicleSubmitAttempted(false);
    setAdvisorSubmitAttempted(false);
  }

  function resetFormFields() {
    setRoNumber("");
    setTagNumber("");
    setVinLast8("");
    setCustomerName("");
    setVehicle("");
    setAdvisorName(defaultAdvisor || "");
    setAdvisorEmploymentId("");
    setWorkMix(WORK_MIX.MAINTENANCE);
    setTransportType(TRANSPORT_TYPES.OTHER);
    setPromiseTimeISO("");
    if (promiseInputRef.current) promiseInputRef.current.value = "";
    resetValidationUi();
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving || isSavingGlobal) return;
    const committedPromiseTime = commitPromiseTime();
    if (committedPromiseTime === undefined) {
      promiseInputRef.current?.reportValidity();
      return;
    }

    setBanner(null);

    setRoSubmitAttempted(true);
    setTagSubmitAttempted(true);
    setVinSubmitAttempted(true);
    setCustomerSubmitAttempted(true);
    setVehicleSubmitAttempted(true);
    setAdvisorSubmitAttempted(true);

    const rn = normalizeRoNumber(roNumber);
    const tag = normalizeTag(tagNumber);
    const vin = normalizeVinLast8(vinLast8);
    const cust = safeString(customerName).trim();
    const veh = safeString(vehicle).trim();
    const adv = safeString(advisorName).trim();

    const validations = [
      [validateRoNow(rn), setRoError],
      [validateTagNow(tag), setTagError],
      [validateVinNow(vin), setVinError],
      [validateCustomerNow(cust), setCustomerError],
      [validateVehicleNow(veh), setVehicleError],
      [validateAdvisorNow(adv), setAdvisorError],
    ];

    const firstBad = validations.find(([res]) => !res.ok);

    if (firstBad) {
      const [res, setter] = firstBad;
      setter(res.message);
      setBanner({ type: "error", message: res.message });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (typeof addROFn !== "function") {
      setBanner({ type: "error", message: "Missing addRO in ROContext.js" });
      return;
    }

    if (isImpersonating) {
      setBanner({
        type: "error",
        message: "RO entry is disabled while impersonating a store user.",
      });
      return;
    }

    try {
      setSaving(true);
      resetValidationUi();
      setBanner({ type: "info", message: "Adding repair order to the WIP Board…" });

      await addROFn({
        roNumber: rn,
        tagNumber: tag,
        vin,
        vinLast8: vin,
        customerName: cust,
        vehicle: veh,
        advisorName: adv,
        advisorEmploymentId: advisorEmploymentId || (matchingAdvisor.length === 1 ? matchingAdvisor[0].employmentId : ""),
        concern: workMixLabel(workMix),
        workMix,
        transportType,
        promiseTimeISO: committedPromiseTime ? new Date(committedPromiseTime).toISOString() : "",
        status: RO_STATUS.AVAILABLE,
        lifecycle: RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT,
        partsStatus: PARTS_STATUS.NONE,
      });

      const savedAt = new Date().toISOString();

      resetFormFields();

      setRecentAdded({
        roNumber: rn,
        tagNumber: tag,
        vinLast8: vin,
        customerName: cust,
        vehicle: veh,
        atISO: savedAt,
      });

      setBanner({
        type: "success",
        message: `RO #${rn} saved and sent to the WIP Board.`,
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setBanner({ type: "error", message: String(err?.message || err) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  function formatRecentWhen(iso) {
    return prettyTime(iso);
  }

  function goEditRecent() {
    if (!recentAdded?.roNumber) return;
    navigate(`/ro-search?q=${encodeURIComponent(recentAdded.roNumber)}`);
  }

  function goDispatchRecent() {
    if (!recentAdded?.tagNumber) return;
    navigate(`/dispatch?tag=${encodeURIComponent(recentAdded.tagNumber)}`);
  }

  return (
    <main style={styles.page} aria-labelledby="ro-entry-title">
      <div style={styles.pageHeader}>
        <div>
          <div style={styles.eyebrow}>Repair Order Intake</div>
          <h1 id="ro-entry-title" style={styles.title}>
            Add Repair Order
          </h1>
        </div>

        <div style={styles.headerMeta} aria-label="RO entry progress">
          <StatusPill tone={isFormReady ? "good" : "neutral"}>
            {formProgress.done}/{formProgress.total} Required fields
          </StatusPill>
          {busy ? <StatusPill tone="working">Saving…</StatusPill> : null}
        </div>
      </div>

      {banner ? (
        <div
          style={bannerStyle(banner.type)}
          role={banner.type === "error" ? "alert" : "status"}
          aria-live={banner.type === "error" ? "assertive" : "polite"}
        >
          {banner.message}
        </div>
      ) : null}

      {recentAdded ? (
        <div style={styles.recentCard} role="status" aria-live="polite">
          <div style={{ minWidth: 0 }}>
            <div style={styles.recentTitle}>
              Recently added: RO #{recentAdded.roNumber} • Tag #{recentAdded.tagNumber}
            </div>
            <div style={styles.recentSub}>
              VIN {recentAdded.vinLast8} • {recentAdded.customerName} •{" "}
              {recentAdded.vehicle} • Saved {formatRecentWhen(recentAdded.atISO)}
            </div>
          </div>

          <div style={styles.recentActions}>
            <button type="button" onClick={goDispatchRecent} style={smallBtnPrimary}>
              Open in WIP
            </button>
            <button type="button" onClick={goEditRecent} style={smallBtn}>
              Search / Edit
            </button>
          </div>
        </div>
      ) : null}

      <div style={styles.layout}>
        <form
          onSubmit={onSubmit}
          style={styles.formCard}
          aria-labelledby="ro-entry-form-title"
          noValidate
        >
          <SectionTitle
            id="ro-entry-form-title"
            title="Core identifiers"
          />

          <div style={styles.twoCol}>
            <div>
              <label htmlFor="ro-entry-ro-number" style={labelStyle}>
                RO Number *
              </label>
              <input
                id="ro-entry-ro-number"
                name="roNumber"
                value={roNumber}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setRoNumber(normalizeRoNumber(e.target.value));
                }}
                onBlur={() => setRoTouched(true)}
                style={inputStyle(roError)}
                placeholder="e.g., 123456"
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!roError}
                aria-describedby={roError ? "ro-entry-ro-number-message" : undefined}
                autoComplete="off"
              />
              <FieldMessage id="ro-entry-ro-number-message" error={roError} />
            </div>

            <div>
              <label htmlFor="ro-entry-tag-number" style={labelStyle}>
                Tag # *
              </label>
              <input
                id="ro-entry-tag-number"
                name="tagNumber"
                value={tagNumber}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setTagNumber(normalizeTag(e.target.value));
                }}
                onBlur={() => setTagTouched(true)}
                style={inputStyle(tagError)}
                placeholder="e.g., 421"
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!tagError}
                aria-describedby={tagError ? "ro-entry-tag-number-message" : undefined}
                autoComplete="off"
              />
              <FieldMessage id="ro-entry-tag-number-message" error={tagError} />
            </div>
          </div>

          <div style={styles.twoCol}>
            <div>
              <label htmlFor="ro-entry-vin-last-8" style={labelStyle}>
                VIN Last 8 *
              </label>
              <input
                id="ro-entry-vin-last-8"
                name="vinLast8"
                value={vinLast8}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setVinLast8(normalizeVinLast8(e.target.value));
                }}
                onBlur={() => setVinTouched(true)}
                style={{ ...inputStyle(vinError), textTransform: "uppercase" }}
                placeholder="e.g., 1A2B3C4D"
                maxLength={8}
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!vinError}
                aria-describedby={vinError ? "ro-entry-vin-last-8-message" : undefined}
                autoComplete="off"
              />
              <FieldMessage id="ro-entry-vin-last-8-message" error={vinError} />
            </div>

            <div>
              <label htmlFor="ro-entry-advisor" style={labelStyle}>
                Advisor *
              </label>
              <select
                id="ro-entry-advisor"
                name="advisorName"
                value={advisorSelectValue}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  applyAdvisorSelection(e.target.value);
                }}
                onBlur={() => setAdvisorTouched(true)}
                style={inputStyle(advisorError)}
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!advisorError}
                aria-describedby={advisorError ? "ro-entry-advisor-message" : undefined}
              >
                <option value="">Select advisor…</option>
                {advisorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FieldMessage id="ro-entry-advisor-message" error={advisorError} />
            </div>
          </div>

          <SectionDivider />

          <SectionTitle
            title="Customer and vehicle"
          />

          <div style={styles.twoCol}>
            <div>
              <label htmlFor="ro-entry-customer" style={labelStyle}>
                Customer *
              </label>
              <input
                id="ro-entry-customer"
                name="customerName"
                value={customerName}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setCustomerName(e.target.value);
                }}
                onBlur={() => setCustomerTouched(true)}
                style={inputStyle(customerError)}
                placeholder="Customer name"
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!customerError}
                aria-describedby={customerError ? "ro-entry-customer-message" : undefined}
                autoComplete="name"
              />
              <FieldMessage id="ro-entry-customer-message" error={customerError} />
            </div>

            <div>
              <label htmlFor="ro-entry-vehicle" style={labelStyle}>
                Vehicle *
              </label>
              <input
                id="ro-entry-vehicle"
                name="vehicle"
                value={vehicle}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setVehicle(e.target.value);
                }}
                onBlur={() => setVehicleTouched(true)}
                style={inputStyle(vehicleError)}
                placeholder="e.g., 2021 Range Rover"
                disabled={busy}
                required
                aria-required="true"
                aria-invalid={!!vehicleError}
                aria-describedby={vehicleError ? "ro-entry-vehicle-message" : undefined}
                autoComplete="off"
              />
              <FieldMessage id="ro-entry-vehicle-message" error={vehicleError} />
            </div>
          </div>

          <SectionDivider />

          <SectionTitle title="Shop routing" />

          <div style={styles.threeCol}>
            <div>
              <label htmlFor="ro-entry-promise-time" style={labelStyle}>
                Promise Time
              </label>
              <input
                id="ro-entry-promise-time"
                name="promiseTime"
                ref={promiseInputRef}
                defaultValue=""
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  // Let the browser retain incomplete segmented edits; never echo them back.
                  setPromiseTimeISO(e.target.validity.badInput ? "" : e.target.value);
                }}
                type="datetime-local"
                onBlur={commitPromiseTime}
                step={60}
                style={inputStyle("")}
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="ro-entry-work-mix" style={labelStyle}>
                Work Mix
              </label>
              <select
                id="ro-entry-work-mix"
                name="workMix"
                value={workMix}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setWorkMix(e.target.value);
                }}
                style={inputStyle("")}
                disabled={busy}
              >
                <option value={WORK_MIX.MAINTENANCE}>Maintenance</option>
                <option value={WORK_MIX.DIAGNOSIS}>Diagnosis</option>
                <option value={WORK_MIX.OTHER}>Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="ro-entry-transport-type" style={labelStyle}>
                Transport Type
              </label>
              <select
                id="ro-entry-transport-type"
                name="transportType"
                value={transportType}
                onChange={(e) => {
                  clearErrorBannerIfAny();
                  setTransportType(e.target.value);
                }}
                style={inputStyle("")}
                disabled={busy}
              >
                <option value={TRANSPORT_TYPES.WAITER}>Waiter</option>
                <option value={TRANSPORT_TYPES.LOANER}>Loaner</option>
                <option value={TRANSPORT_TYPES.SHUTTLE}>Shuttle</option>
                <option value={TRANSPORT_TYPES.OTHER}>Other</option>
              </select>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              type="submit"
              style={buttonStyle(busy, isFormReady)}
              disabled={busy}
              aria-disabled={busy}
            >
              {busy ? "Adding to WIP…" : "Add to WIP"}
            </button>
            <button
              type="button"
              style={secondaryBtn}
              onClick={resetFormFields}
              disabled={busy}
              aria-disabled={busy}
            >
              Clear form
            </button>
          </div>
        </form>

        <aside style={styles.sideCard} aria-labelledby="ro-entry-summary-title">
          <div id="ro-entry-summary-title" style={styles.sideTitle}>
            RO Summary
          </div>
          <div style={styles.summaryList}>
            <SummaryLine label="RO #" value={roNumber || "—"} />
            <SummaryLine label="Tag" value={tagNumber || "—"} />
            <SummaryLine label="VIN Last 8" value={vinLast8 || "—"} />
            <SummaryLine label="Customer" value={customerName || "—"} />
            <SummaryLine label="Vehicle" value={vehicle || "—"} />
            <SummaryLine label="Advisor" value={advisorName || "—"} />
            <SummaryLine label="Promise" value={promiseDisplay(promiseTimeISO)} />
            <SummaryLine label="Transport" value={getTransportLabel(transportType)} />
            <SummaryLine label="Job" value={workMixLabel(workMix)} />
          </div>

          <div style={styles.readyBox}>
            <div style={styles.readyTop}>
              <span>Ready for WIP</span>
              <StatusPill tone={isFormReady ? "good" : "warn"}>
                {isFormReady ? "Ready" : "Incomplete"}
              </StatusPill>
            </div>
            <div
              style={styles.progressTrack}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={formProgress.percent}
              aria-label="Required field completion"
            >
              <div style={{ ...styles.progressFill, width: `${formProgress.percent}%` }} />
            </div>
            <div style={styles.readySub}>
              {isFormReady
                ? "All required fields are complete. This RO is ready for the WIP Board."
                : `${formProgress.total - formProgress.done} required field${
                    formProgress.total - formProgress.done === 1 ? "" : "s"
                  } remaining.`}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function SectionTitle({ id, title, subtitle }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div id={id} style={styles.sectionTitle}>
        {title}
      </div>
      {subtitle ? <div style={styles.sectionSub}>{subtitle}</div> : null}
    </div>
  );
}

function SectionDivider() {
  return <div style={styles.divider} />;
}

function SummaryLine({ label, value }) {
  return (
    <div style={styles.summaryLine}>
      <span style={styles.summaryLabel}>{label}</span>
      <span style={styles.summaryValue} title={safeString(value)}>
        {value}
      </span>
    </div>
  );
}

function bannerStyle(type) {
  const base = {
    marginBottom: 8,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,0.08)",
    fontWeight: 850,
    fontSize: 12,
    boxShadow: "0 6px 18px rgba(15,23,42,0.035)",
  };

  if (type === "success") {
    return {
      ...base,
      border: "1px solid rgba(6,118,71,0.14)",
      background: "linear-gradient(180deg, rgba(236,253,243,0.94), #fff)",
      color: "#067647",
    };
  }

  if (type === "error") {
    return {
      ...base,
      border: "1px solid rgba(180,35,24,0.14)",
      background: "linear-gradient(180deg, rgba(254,243,242,0.96), #fff)",
      color: "#b42318",
    };
  }

  return {
    ...base,
    border: "1px solid rgba(37,99,235,0.14)",
    background: "linear-gradient(180deg, rgba(239,246,255,0.94), #fff)",
    color: "#1d4ed8",
  };
}

const labelStyle = {
  display: "block",
  fontWeight: 850,
  fontSize: 11,
  marginBottom: 4,
  color: "rgba(15,23,42,0.78)",
};

function inputStyle(error) {
  return {
    width: "100%",
    height: 34,
    padding: "0 10px",
    borderRadius: 9,
    border: error
      ? "1px solid rgba(180,35,24,0.32)"
      : "1px solid rgba(15,23,42,0.10)",
    background: error ? "rgba(254,243,242,0.72)" : "#ffffff",
    color: "rgba(15,23,42,0.90)",
    fontWeight: 650,
    fontSize: 12.2,
    outline: "none",
    fontFamily: "inherit",
    boxShadow: error ? "0 0 0 3px rgba(180,35,24,0.045)" : "none",
  };
}

function buttonStyle(busy, ready) {
  return {
    height: 34,
    padding: "0 14px",
    borderRadius: 10,
    border: ready ? "1px solid #111827" : "1px solid rgba(15,23,42,0.12)",
    background: ready ? "#111827" : "#ffffff",
    color: ready ? "#ffffff" : "rgba(15,23,42,0.72)",
    cursor: busy ? "not-allowed" : "pointer",
    fontWeight: 900,
    fontSize: 12.5,
    opacity: busy ? 0.62 : 1,
    boxShadow: ready ? "0 10px 22px rgba(17,24,39,0.12)" : "none",
  };
}

const secondaryBtn = {
  height: 34,
  padding: "0 13px",
  borderRadius: 10,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "#ffffff",
  color: "rgba(15,23,42,0.78)",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 12.5,
};

const smallBtn = {
  height: 30,
  padding: "0 10px",
  borderRadius: 9,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "white",
  cursor: "pointer",
  fontWeight: 850,
  fontSize: 11.5,
  color: "rgba(15,23,42,0.78)",
};

const smallBtnPrimary = {
  ...smallBtn,
  border: "1px solid #111827",
  background: "#111827",
  color: "#fff",
};

const styles = {
  page: {
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
    flexWrap: "wrap",
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.52)",
    marginBottom: 3,
  },
  title: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.02,
    letterSpacing: "-0.035em",
    color: "rgba(15,23,42,0.94)",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 11.5,
    color: "rgba(15,23,42,0.58)",
    lineHeight: 1.32,
  },
  headerMeta: {
    display: "flex",
    gap: 7,
    alignItems: "center",
    flexWrap: "wrap",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.38fr)",
    gap: 10,
    alignItems: "start",
  },
  formCard: {
    display: "grid",
    gap: 8,
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 14,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    padding: 12,
    minWidth: 0,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  threeCol: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.72)",
  },
  sectionSub: {
    marginTop: 3,
    fontSize: 10.8,
    color: "rgba(15,23,42,0.54)",
    lineHeight: 1.25,
  },
  divider: {
    height: 1,
    background: "rgba(15,23,42,0.07)",
    margin: 0,
  },
  fieldError: {
    marginTop: 4,
    fontSize: 10.7,
    color: "#b42318",
    fontWeight: 800,
    lineHeight: 1.18,
  },
  formActions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    paddingTop: 0,
  },
  sideCard: {
    position: "sticky",
    top: 10,
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 14,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    padding: 11,
    minWidth: 0,
  },
  sideTitle: {
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.72)",
    marginBottom: 8,
  },
  summaryList: {
    display: "grid",
    gap: 5,
  },
  summaryLine: {
    display: "grid",
    gridTemplateColumns: "82px minmax(0, 1fr)",
    gap: 8,
    alignItems: "baseline",
    borderBottom: "1px solid rgba(15,23,42,0.055)",
    paddingBottom: 5,
  },
  summaryLabel: {
    fontSize: 10.2,
    fontWeight: 850,
    color: "rgba(15,23,42,0.44)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  summaryValue: {
    minWidth: 0,
    fontSize: 11,
    fontWeight: 800,
    color: "rgba(15,23,42,0.80)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  readyBox: {
    marginTop: 10,
    border: "1px solid rgba(15,23,42,0.07)",
    borderRadius: 12,
    padding: 9,
    background: "linear-gradient(180deg, #ffffff, #fbfcfd)",
  },
  readyTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 11.4,
    fontWeight: 900,
    color: "rgba(15,23,42,0.82)",
  },
  readySub: {
    marginTop: 6,
    fontSize: 10.8,
    color: "rgba(15,23,42,0.55)",
    lineHeight: 1.25,
  },
  progressTrack: {
    marginTop: 8,
    height: 7,
    borderRadius: 999,
    background: "rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    background:
      "linear-gradient(90deg, rgba(37,99,235,0.42), rgba(37,99,235,0.74))",
    transition: "width 160ms ease",
  },
  recentCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    border: "1px solid rgba(6,118,71,0.14)",
    borderRadius: 12,
    padding: "8px 10px",
    background: "linear-gradient(180deg, rgba(236,253,243,0.86), #fff)",
    boxShadow: "0 6px 18px rgba(15,23,42,0.035)",
  },
  recentTitle: {
    fontSize: 12,
    fontWeight: 900,
    color: "#067647",
    lineHeight: 1.2,
  },
  recentSub: {
    marginTop: 2,
    fontSize: 10.8,
    color: "rgba(15,23,42,0.58)",
    lineHeight: 1.25,
  },
  recentActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
};
